// LinkHoldersPanel — "Has a link, not on the team", under the team in the
// Team box (v0.300.0).
//
// Brett: "If you are counting that then you need to add to the in the team
// section so we know to remove, otherwise there is no way of easily knowing".
// Test links made to someone never on the team were counted by the unsent
// banner and shown nowhere else but the Shares list. This lists each person
// (by address) holding a live link to the project who is not on its team, and
// turns their links off.
//
// Props:
//   projectId      — required.
//   team           — the team as read, or null while it has not been read (or
//                    could not be): without it nobody can be called "not on the
//                    team", so the panel says so instead of listing everyone.
//   onLinksChange  — optional. Called after links are turned off, so the
//                    register's strip reads them again.
//
// States, each in its own words: waiting on the team, checking the links,
// could not read them (Try again), everyone with a link is on the team, and
// the list.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiGet, apiSend, fmtDate } from '../api';
import { alertFailure } from '../utils/sessionEnd';
import { theme } from '../theme';
import { strayLinkHolders } from '../utils/linkHolders';

const ACCESS = { view: 'View only', reply: 'Reply' };

// v0.339.0 — reportId: the team is the report's, so only that report's links
// are weighed against it (the server lists the location's; filtered here).
export default function LinkHoldersPanel({ projectId, reportId = null, team, onLinksChange }) {
  const [links, setLinks] = useState(null);
  const [state, setState] = useState('loading'); // 'loading' | 'ok' | 'failed'
  const [err, setErr] = useState('');
  const [busyKey, setBusyKey] = useState(null);

  const load = useCallback(async () => {
    setState('loading');
    try {
      const rows = await apiGet(`/api/report-shares?projectId=${encodeURIComponent(projectId)}`, { fresh: true });
      const all = Array.isArray(rows) ? rows : [];
      setLinks(reportId == null ? all : all.filter((r) => String(r.reportId) === String(reportId)));
      setState('ok');
    } catch (e) {
      setErr(e?.message || String(e));
      setState('failed');
    }
  }, [projectId, reportId]);
  useEffect(() => { if (projectId) load(); }, [projectId, load]);

  const stray = useMemo(() => strayLinkHolders(links, team), [links, team]);

  async function turnOff(g) {
    const who = g.email || 'anyone holding a link with no address on it';
    const n = g.links.length;
    if (!window.confirm(`Turn off ${n === 1 ? 'the link' : `all ${n} links`} for ${who}? `
      + `${n === 1 ? 'It stops' : 'They stop'} opening straight away. Messages already written stay.`)) return;
    setBusyKey(g.key);
    let failed = 0;
    let why = '';
    for (const l of g.links) {
      try {
        await apiSend('PATCH', `/api/report-shares/${l.id}`, { revokedAt: 'now' }, { noQueue: true }); // eslint-disable-line no-await-in-loop
      } catch (e) {
        failed += 1;
        why = e?.message || String(e);
      }
    }
    setBusyKey(null);
    if (failed) alertFailure(`${failed} of ${n} link${n === 1 ? '' : 's'} could not be turned off: ${why}`, null);
    await load();
    if (onLinksChange) onLinksChange();
  }

  const quiet = { fontSize: 12, color: theme.textLight, padding: '6px 0' };
  const textBtn = {
    border: 0, background: 'transparent', padding: 0, cursor: 'pointer',
    fontSize: 12, fontWeight: 700, fontFamily: 'inherit',
  };

  let body;
  if (!Array.isArray(team)) {
    body = <div style={quiet}>Shown once the team has been read.</div>;
  } else if (state === 'failed') {
    body = (
      <div style={{ padding: 8, background: theme.bg, border: `1px solid ${theme.danger}`, color: theme.danger, borderRadius: 6, fontSize: 12 }}>
        Who has a link could not be read, so nobody is shown: {err}{' '}
        <button type="button" onClick={load} style={{ ...textBtn, color: theme.brand }}>Try again</button>
      </div>
    );
  } else if (state === 'loading' && !links) {
    body = <div style={quiet}>Checking who has a link…</div>;
  } else if (!stray.length) {
    body = <div style={quiet}>Everyone with a link to this report is on its team.</div>;
  } else {
    body = (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {stray.map((g) => {
          const n = g.links.length;
          const access = g.permissions.map((p) => ACCESS[p] || p).join(', ');
          return (
            <div key={g.key || '(none)'} data-part="link-holder" style={{
              display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
              padding: '6px 8px', border: `1px solid ${theme.border}`, borderRadius: 6,
              fontSize: 13, opacity: busyKey === g.key ? 0.5 : 1,
            }}>
              <span style={{ flex: '1 1 220px', minWidth: 0 }}>
                <span style={{ display: 'block', fontWeight: 600, color: theme.text, overflowWrap: 'anywhere' }}>
                  {g.email || 'Anyone with the link (no address on it)'}
                </span>
                <span style={{ display: 'block', fontSize: 12, color: theme.textLight }}>
                  {n} link{n === 1 ? '' : 's'}{access ? ` · ${access}` : ''}
                  {' · '}{g.lastUsedAt ? `last opened ${fmtDate(g.lastUsedAt)}` : 'never opened'}
                </span>
              </span>
              <button
                type="button"
                onClick={() => turnOff(g)}
                disabled={busyKey != null}
                style={{ ...textBtn, color: theme.danger }}
                title="Turn these links off. The person is not added to or removed from anything else."
              >Turn off {n === 1 ? 'link' : 'links'}</button>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div data-part="link-holders" data-state={!Array.isArray(team) ? 'no-team' : state} style={{ marginTop: 16, paddingTop: 12, borderTop: `1px solid ${theme.border}` }}>
      <div style={{ fontSize: 11, fontWeight: 800, color: theme.textLight, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        Has a link, not on the team{stray.length && Array.isArray(team) ? ` · ${stray.length}` : ''}
      </div>
      <div style={{ fontSize: 12, color: theme.textLight, margin: '2px 0 8px' }}>
        People holding a live link to this report who are not on its team. Add them to the team, or turn their links off.
      </div>
      {body}
    </div>
  );
}
